/**
 * One hint in a session's timeline.
 *
 * Reads top-to-bottom in the order the moment actually happened: what the
 * student had on the page → what they asked for → what the model made of it →
 * the hint it gave → what the student thought of it. That ordering is the point
 * of the card: a reviewer judging hint quality needs the input before the output.
 *
 * A prewritten reveal renders a deliberately thinner card — it has no
 * diagnosis, no level and no work snapshot, and pretending otherwise with
 * empty rows would read as missing data rather than as "nothing to show".
 */

import { useState } from 'react';
import { LatexText } from '../latex';
import { ImageViewer } from '../components';
import {
  HINT_CATEGORY_LABELS, HINT_STATE_LABELS,
  type HintQualityTurn, type HintReviewerFeedback,
} from './types';

function timeOf(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatStamp(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString([], { day: 'numeric', month: 'short' }) +
    ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
}

/** A rating always names its rater and when — "👍" alone leaves a reviewer
 *  guessing whether the student said it, and whether it was before or after
 *  some change to the hint. */
function Rating({ feedback, by }: { feedback?: HintQualityTurn['feedback']; by?: string }) {
  if (!feedback) return null;
  const up = feedback.rating === 'positive';
  const stamp = formatStamp(feedback.submittedAt);
  return (
    <span className={`hintq-rating ${up ? 'hintq-rating--up' : 'hintq-rating--down'}`} title={feedback.comment}>
      {up ? '👍 Helpful' : '👎 Not helpful'}
      <span className="hintq-rating-by">
        {by ? ` — ${by}` : ''}{stamp ? `, ${stamp}` : ''}
      </span>
    </span>
  );
}

/** The reviewer's own verdict: thumbs, an optional note on thumbs-down, and —
 *  once set — who said it and when. Separate from the student's rating above,
 *  and labelled as such, because the whole point is telling them apart. */
function ReviewControl({ turn, onReview }: {
  turn: HintQualityTurn;
  onReview?: (rating: 'positive' | 'negative', comment?: string) => Promise<void>;
}) {
  const current = turn.reviewerFeedback;
  const [pending, setPending] = useState<'positive' | 'negative' | null>(null);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!onReview) {
    // Read-only render — still show an existing verdict.
    return current ? <ReviewVerdict verdict={current} /> : null;
  }

  const submit = async (rating: 'positive' | 'negative', note?: string) => {
    setPending(rating);
    setError(null);
    try {
      await onReview(rating, note);
      setShowComment(false);
      setComment('');
    } catch (e) {
      setError((e as Error).message || 'Could not save');
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="hintq-review">
      <span className="hintq-review-label">Your verdict on this hint</span>
      <div className="hintq-review-actions">
        <button
          type="button"
          className={`hintq-review-btn ${current?.rating === 'positive' ? 'is-active is-up' : ''}`}
          disabled={!!pending}
          onClick={() => submit('positive')}
        >
          👍 Good hint
        </button>
        <button
          type="button"
          className={`hintq-review-btn ${current?.rating === 'negative' ? 'is-active is-down' : ''}`}
          disabled={!!pending}
          // Thumbs-down asks for a reason before saving — it also opens a triage
          // thread, and "bad hint" with no reason is not actionable.
          onClick={() => setShowComment(v => !v)}
        >
          👎 Poor hint
        </button>
        {current && <ReviewVerdict verdict={current} />}
      </div>
      {showComment && (
        <div className="hintq-review-comment">
          <textarea
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="What's wrong with this hint? (optional)"
            rows={2}
          />
          <button
            type="button"
            className="btn-primary"
            disabled={!!pending}
            onClick={() => submit('negative', comment.trim() || undefined)}
          >
            {pending ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
      {error && <p className="hintq-review-error">{error}</p>}
    </div>
  );
}

function ReviewVerdict({ verdict }: { verdict: HintReviewerFeedback }) {
  const up = verdict.rating === 'positive';
  return (
    <span
      className={`hintq-rating ${up ? 'hintq-rating--up' : 'hintq-rating--down'}`}
      title={verdict.comment}
    >
      {up ? '👍 Good hint' : '👎 Poor hint'}
      <span className="hintq-rating-by">
        {` — ${verdict.reviewerName} (${verdict.reviewerRole}), ${formatStamp(verdict.submittedAt)}`}
      </span>
    </span>
  );
}

export function HintTurnCard({ turn, studentName, onReview }: {
  turn: HintQualityTurn;
  studentName?: string;
  /** Omitted in read-only contexts; present makes the card ratable. */
  onReview?: (rating: 'positive' | 'negative', comment?: string) => Promise<void>;
}) {
  if (turn.source === 'prewritten') {
    return (
      <div className="hintq-turn hintq-turn--prewritten">
        <div className="hintq-turn-head">
          <span className="hintq-turn-label">Ready-made hint #{turn.hintIndex + 1}</span>
          <span className="hintq-turn-time">{timeOf(turn.createdAt)}</span>
          <Rating feedback={turn.feedback} by={studentName} />
        </div>
        <div className="hintq-hint"><LatexText text={turn.hint} /></div>
        {turn.feedback?.comment && (
          <p className="hintq-comment">“{turn.feedback.comment}”</p>
        )}
        <ReviewControl turn={turn} onReview={onReview} />
      </div>
    );
  }

  const hasWork = !!(turn.typedTextAtHintTime || turn.canvasImageUrl || turn.yourProgress);

  return (
    <div className="hintq-turn">
      <div className="hintq-turn-head">
        <span className="hintq-turn-label">Hint {turn.turnIndex}</span>
        {turn.level && <span className={`hintq-badge hintq-level--${turn.level}`}>{turn.level}</span>}
        {turn.category && (
          <span className="hintq-badge hintq-badge--muted">
            asked: {HINT_CATEGORY_LABELS[turn.category] || turn.category}
          </span>
        )}
        <span className="hintq-turn-time">{timeOf(turn.createdAt)}</span>
        <Rating feedback={turn.feedback} by={studentName} />
      </div>

      {turn.studentMessage && (
        <p className="hintq-studentmsg">Student said: “{turn.studentMessage}”</p>
      )}

      {hasWork && (
        <section className="hintq-section">
          <h5 className="hintq-section-title">Where the student was</h5>
          {turn.yourProgress && <p className="hintq-progress">{turn.yourProgress}</p>}
          {turn.typedTextAtHintTime && (
            <pre className="hintq-typed">{turn.typedTextAtHintTime}</pre>
          )}
          {turn.canvasImageUrl && (
            <ImageViewer src={turn.canvasImageUrl} alt={`Student work at hint ${turn.turnIndex}`} className="hintq-canvas" />
          )}
          {!turn.canvasImageUrl && turn.hadImageAtHintTime && (
            <p className="hintq-muted">The student had drawn something, but the snapshot wasn't recorded.</p>
          )}
        </section>
      )}
      {!hasWork && (
        <p className="hintq-muted hintq-section">The student had nothing on the page yet.</p>
      )}

      {(turn.detectedState || turn.detectedIssue) && (
        <section className="hintq-section">
          <h5 className="hintq-section-title">What the AI diagnosed</h5>
          <div className="hintq-diagnosis">
            {turn.detectedState && (
              <span className={`hintq-badge hintq-state--${turn.detectedState}`}>
                {HINT_STATE_LABELS[turn.detectedState] || turn.detectedState}
              </span>
            )}
            {turn.difficulty && <span className="hintq-badge hintq-badge--muted">{turn.difficulty} step</span>}
            {turn.workChanged === false && (
              <span className="hintq-badge hintq-badge--muted" title="No change in the student's work since the previous hint">
                no new work
              </span>
            )}
            {typeof turn.confidence === 'number' && (
              <span className="hintq-badge hintq-badge--muted">confidence {Math.round(turn.confidence * 100)}%</span>
            )}
          </div>
          {turn.detectedIssue && <p className="hintq-issue">{turn.detectedIssue}</p>}
        </section>
      )}

      <section className="hintq-section">
        <h5 className="hintq-section-title">Hint shown</h5>
        <div className="hintq-hint"><LatexText text={turn.hint} /></div>
      </section>

      {turn.feedback?.comment && (
        <p className="hintq-comment">{studentName || 'Student'} said: “{turn.feedback.comment}”</p>
      )}

      <ReviewControl turn={turn} onReview={onReview} />
    </div>
  );
}
